import CustomDashboardApiHandler from '@/api/custom-dashboard'
import HomePageApiHandler from '@/api/homepage'
import {
  DASHBOARD_SET_DASHBOARD_LIST,
  DASHBOARD_SET_DASHBOARD_ITEM,
  TOGGLE_FAVORITE,
  DASHBOARD_SET_FAVORITE_DASHBOARD_LIST,
  DASHBOARD_SET_COUNT,
  SET_FILTERS
} from '@/store/mutation-types'
import { tracker } from '@/tracking/amplitude'
import { EVENT_CATEGORIES, EVENT_NAMES } from '@/tracking/amplitude-helpers'
import { normalizeDashboardItem } from '@/utils/custom-dashboard-helper'
import { handleError } from '@/utils/error-handling'

const state = {
  dashboardList: [],
  favoriteList: [],
  dashboardItem: {},
  dashboardTotalCount: 0,
  filters: {} // { page, pageSize, search, sort, order } need to sync requests from AppMenu.vue and DashboardList.vue
}

const getters = {}

const setDashboardItem = ({ commit, data, moduleParams = [] }) => {
  const dashboardItem = normalizeDashboardItem({
    dashboardItem: data,
    moduleParams
  })

  commit(DASHBOARD_SET_DASHBOARD_ITEM, dashboardItem)

  return dashboardItem
}

const actions = {
  async getFavoriteList({ commit, rootGetters }, payload = {}) {
    const { showCustomDashboardForUsers } = rootGetters['system/dashboardsVisibility']

    if (!showCustomDashboardForUsers) {
      commit(DASHBOARD_SET_FAVORITE_DASHBOARD_LIST, [])
    } else {
      const customDashboardApi = new CustomDashboardApiHandler()
      try {
        const data = await customDashboardApi.getDashboardList(payload)

        commit(DASHBOARD_SET_FAVORITE_DASHBOARD_LIST, data)
      } catch (error) {
        handleError({ error })
      }
    }
  },

  async getDashboardList({ commit, dispatch, state, rootGetters }) {
    const { showCustomDashboardForUsers } = rootGetters['system/dashboardsVisibility']

    if (!showCustomDashboardForUsers) {
      commit(DASHBOARD_SET_DASHBOARD_LIST, [])
    } else {
      const customDashboardApi = new CustomDashboardApiHandler()
      try {
        dispatch('getDashboardCount', state.filters)
        const data = await customDashboardApi.getDashboardList(state.filters)
        commit(DASHBOARD_SET_DASHBOARD_LIST, data)
      } catch (error) {
        handleError({ error })
      }
    }
  },

  async getDashboardCount({ commit }, payload) {
    const customDashboardApi = new CustomDashboardApiHandler()
    try {
      const { count } = await customDashboardApi.getDashboardCount(payload)

      commit(DASHBOARD_SET_COUNT, count)
    } catch (error) {
      handleError({ error })
    }
  },

  async getDashboardItemByGroupId({ commit }, { groupId = null, moduleParams = [] } = {}) {
    if (!groupId) {
      return
    }

    const homePageApi = new HomePageApiHandler()
    const data = await homePageApi.getDashboardByGroupId({ groupId, moduleParams })

    // null response means that dashboard is not available for current user
    if (!data) {
      return null
    }

    return setDashboardItem({ commit, data, moduleParams })
  },

  async getDashboardItem({ commit, state }, { id = null, moduleParams = [] } = {}) {
    const dashboardId = id || state.dashboardItem.id
    if (!dashboardId) {
      return
    }

    const customDashboardApi = new CustomDashboardApiHandler()
    // eslint-disable-next-line no-useless-catch
    try {
      const data = await customDashboardApi.getDashboard({ id: dashboardId, moduleParams })

      return setDashboardItem({ commit, data, moduleParams })
    } catch (error) {
      throw error
    }
  },

  async toggleFavorite({ commit }, payload) {
    const customDashboardApi = new CustomDashboardApiHandler()
    try {
      if (payload.favorite) {
        await customDashboardApi.removeFromFavorites({ dashboardIds: [payload.id] })
        tracker.logEvent(EVENT_NAMES.CHANGE_FAVORITES, {
          category: EVENT_CATEGORIES.DASHBOARD,
          mode: 'removed',
          id: payload.id
        })
      } else {
        await customDashboardApi.addToFavorites({ dashboardIds: [payload.id] })
        tracker.logEvent(EVENT_NAMES.CHANGE_FAVORITES, {
          category: EVENT_CATEGORIES.DASHBOARD,
          mode: 'added',
          id: payload.id
        })
      }
      commit(TOGGLE_FAVORITE, payload)
    } catch (error) {
      handleError({ error })
    }
  },

  setFilters({ commit }, payload = {}) {
    commit(SET_FILTERS, payload)
  }
}

const mutations = {
  [DASHBOARD_SET_FAVORITE_DASHBOARD_LIST](state, favoriteList) {
    state.favoriteList = favoriteList
  },

  [DASHBOARD_SET_DASHBOARD_LIST](state, dashboardList) {
    state.dashboardList = dashboardList
  },

  [DASHBOARD_SET_DASHBOARD_ITEM](state, dashboardItem) {
    state.dashboardItem = {
      ...dashboardItem,
      modules: dashboardItem.modules.map(module => {
        return {
          ...module,
          elements: module.elements.map(item => {
            const { element, ...rest } = item

            return {
              ...element,
              ...rest
            }
          })
        }
      })
    }
  },

  [TOGGLE_FAVORITE](state, payload) {
    const toggleFavorite = (list, item) => {
      const index = list.findIndex(dashboard => dashboard.id === item.id)
      if (index !== -1) {
        list[index].favorite = !list[index].favorite
      }
    }

    toggleFavorite(state.dashboardList, payload)

    toggleFavorite(state.favoriteList, payload)
  },

  [DASHBOARD_SET_COUNT](state, dashboardTotalCount) {
    state.dashboardTotalCount = dashboardTotalCount
  },

  [SET_FILTERS](state, payload) {
    state.filters = payload
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
